/* eslint-disable complexity */
import { Vector3 } from 'three';
import { IBuildingProviderContext, ITexture } from '../../@types/ContextProviderRelated';
import { EWall } from '../../@types/Enums';
import Boundary, { BOUNDARY_TYPE } from '../../models/abstract/Boundary';
import BoundaryEdge from '../../models/abstract/BoundaryEdge';
import Wall from '../../models/abstract/Wall';
import { IRelation } from '../../models/Relation';
import BuildingLevelModel from '../../models/standard/BuildingLevelModel';
import BuildingModel, { IMultiRoomBuilding } from '../../models/standard/BuildingModel';
import CameraPositionSpriteModel from '../../models/standard/CameraPositionSpriteModel';
import CompassModel from '../../models/standard/CompassModel';
import DoorModel from '../../models/standard/DoorModel';
import ElementModel from '../../models/standard/ElementModel';
import FloorModel from '../../models/standard/FloorModel';
import HotspotModel from '../../models/standard/HotspotModel';
import Model2DModel from '../../models/standard/Model2DModel';
import PassageModel from '../../models/standard/PassageModel';
import PartitionWallModel from '../../models/standard/PartitionWallModel';
import PhysicalBoundaryModel from '../../models/standard/PhysicalBoundary';
import PlanModel from '../../models/standard/PlanModel';
import ProductModel from '../../models/standard/ProductModel';
import RadiatorModel from '../../models/standard/RadiatorModel';
import RoomModel from '../../models/standard/RoomModel';
import SimpleMeasurementModel from '../../models/standard/SimpleMeasurementModel';
import SpaceModel from '../../models/standard/SpaceModel';
import StandardWallModel, { IStandardWall } from '../../models/standard/StandardWallModel';
import TilesModel from '../../models/standard/TileModel';
import VirtualBoundaryModel from '../../models/standard/VirtualBoundaryModel';
import WindowModel from '../../models/standard/WindowModel';
import { NonFetchableEntity } from '../planJsonExportImportUtils';
import { createAttributes, createObject, createObjects } from '../transformation/tranformToMultiRoomBuilding';
import { transformMultiRoomStructure } from '../transformation/transformMultiRoomStructure';
import deserializeHotspot from './deserializeHotspot';
import deserializeMeasurement from './deserializeMeasurement';
import { fetchProductById } from './fetchProductById';
import { fetchTextureFromApi } from './fetchTextureFromApi';
import { instantiateElementAsync } from './instantiateElementAsync';
import { instantiateModel2DAsync } from './instantiateModel2DAsync';

/**
 * Multi-room importer — fully correct, globally consistent, reversible.
 */
export const importPlanJsonV3 = async (
  jsonString: string,
): Promise<{
  plan: PlanModel;
  building: IBuildingProviderContext;
  measurements: SimpleMeasurementModel[];
  cameraPositionSprites: CameraPositionSpriteModel[];
  hotspots: HotspotModel[];
  nonFetchableEntities: Array<NonFetchableEntity>;
  relations: Array<IRelation>;
}> => {
  // -----------------------
  // PARSE ROOT JSON
  // -----------------------
  const data = JSON.parse(jsonString);
  const planData = data.plan;
  const prevNonFetch = planData.nonFetchableEntities ?? [];
  const buildingData = planData.building as IMultiRoomBuilding;
  const compassRotation = planData.compassRotation ?? 0;
  const relations = planData.relations ?? [];

  const auth = true;
  const nonFetchableEntities: NonFetchableEntity[] = [];

  const buildingAny = buildingData as any;

  // ----------------------------------------------------------
  // FIRST PASS — GLOBAL WALL REGISTRY (UUID → full instance)
  // ----------------------------------------------------------
  const rawWallMap = new Map<string, any>(); // raw JSON wallData
  const wallMap = new Map<string, Wall>(); // actual Wall instances

  // Collect **all raw walls** from spaces
  buildingData.levels.forEach((level) => {
    level.rooms.forEach((room) => {
      room.spaces.forEach((space) => {
        (space.walls ?? []).forEach((w) => {
          if (w.uuid && !rawWallMap.has(w.uuid)) rawWallMap.set(w.uuid, w);
        });
      });
    });
  });

  // Also collect floating walls from each level (new structure: level.walls)
  buildingData.levels.forEach((level) => {
    (level as any).walls?.forEach((w: any) => {
      if (w.uuid && !rawWallMap.has(w.uuid)) {
        rawWallMap.set(w.uuid, w);
      }
    });
  });

  // Backwards compatibility: legacy root walls on building (old structure)
  (buildingAny.walls ?? []).forEach((w: any) => {
    if (w.uuid && !rawWallMap.has(w.uuid)) {
      rawWallMap.set(w.uuid, w);
    }
  });

  // Build real Wall instances globally
  rawWallMap.forEach((wallData, wallUuid) => {
    const startPoint = new Vector3(wallData.startPoint.x, wallData.startPoint.y, wallData.startPoint.z);
    const endPoint = new Vector3(wallData.endPoint.x, wallData.endPoint.y, wallData.endPoint.z);

    // Doors
    const wallDoors = (wallData.doors ?? []).map((door: any) => {
      const d = new DoorModel(
        new Vector3(door.position.x, door.position.y, door.position.z),
        door.length,
        door.depth,
        door.angle,
        door.openingDirection,
        door.orientation,
        createAttributes(door.attributes),
      );
      d.setUUID(door.uuid);
      return d;
    });

    const wallPassages = (wallData.passages ?? []).map((passage: any) => {
      const p = new PassageModel(
        new Vector3(passage.position.x, passage.position.y, passage.position.z),
        passage.length,
        passage.depth,
        passage.angle,
        createAttributes(passage.attributes),
      );
      p.setUUID(passage.uuid);
      return p;
    });

    let wallRadA: RadiatorModel[] = [];
    let wallRadB: RadiatorModel[] = [];

    if (wallData.sides) {
      // --- SIDE A ---
      const radA = wallData.sides.A?.radiators;
      if (Array.isArray(radA)) {
        wallRadA = radA.map((rad: any) => {
          const r = new RadiatorModel(
            new Vector3(rad.position.x, rad.position.y, rad.position.z),
            rad.length,
            rad.depth,
            rad.angle,
            createAttributes(rad.attributes),
            undefined,
            rad.nicheDepth,
          );
          r.setUUID(rad.uuid);
          return r;
        });
      }

      // --- SIDE B ---
      const radB = wallData.sides.B?.radiators;
      if (Array.isArray(radB)) {
        wallRadB = radB.map((rad: any) => {
          const r = new RadiatorModel(
            new Vector3(rad.position.x, rad.position.y, rad.position.z),
            rad.length,
            rad.depth,
            rad.angle,
            createAttributes(rad.attributes),
            undefined,
            rad.nicheDepth,
          );
          r.setUUID(rad.uuid);
          return r;
        });
      }
    } else if (Array.isArray(wallData.radiators)) {
      // Legacy format
      wallRadA = wallData.radiators.map((rad: any) => {
        const r = new RadiatorModel(
          new Vector3(rad.position.x, rad.position.y, rad.position.z),
          rad.length,
          rad.depth,
          rad.angle,
          createAttributes(rad.attributes),
          undefined,
          rad.nicheDepth,
        );
        r.setUUID(rad.uuid);
        return r;
      });
    }

    // Windows
    const wallWin = (wallData.windows ?? []).map((win: any) => {
      const w = new WindowModel(new Vector3(win.position.x, win.position.y, win.position.z), win.length, win.depth, win.angle, createAttributes(win.attributes));
      w.setUUID(win.uuid);
      return w;
    });

    let wall: Wall;

    if (wallData.type === EWall.STANDARD) {
      const sw = wallData as IStandardWall;

      const outerStart = new Vector3(sw.outerStartPoint.x, sw.outerStartPoint.y, sw.outerStartPoint.z);
      const outerEnd = new Vector3(sw.outerEndPoint.x, sw.outerEndPoint.y, sw.outerEndPoint.z);
      const outerStart2 = new Vector3(sw.outerStartPoint2.x, sw.outerStartPoint2.y, sw.outerStartPoint2.z);
      const outerEnd2 = new Vector3(sw.outerEndPoint2.x, sw.outerEndPoint2.y, sw.outerEndPoint2.z);

      const w = new StandardWallModel(startPoint, endPoint, sw.depth, wallWin, wallDoors, [], createAttributes(sw.attributes), wallUuid, wallPassages);

      w.setUUID(wallUuid);
      w.setOuterStartPoint(outerStart);
      w.setOuterStartPoint2(outerStart2);
      w.setOuterEndPoint(outerEnd);
      w.setOuterEndPoint2(outerEnd2);

      w.sides.A.radiators = wallRadA;
      w.sides.B.radiators = wallRadB;
      wall = w;
    } else {
      // PARTITION WALL
      const w = new PartitionWallModel(
        startPoint, 
        endPoint, 
        wallData.depth, 
        wallWin, // Windows
        wallDoors, // Doors
        [], // Legacy radiators (empty)
        createAttributes(wallData.attributes),
        wallUuid,
        wallPassages,
      );

      // FIX: Manually assign radiators to sides
      w.sides.A.radiators = wallRadA;
      w.sides.B.radiators = wallRadB;
      
      wall = w;
    }

    wallMap.set(wallUuid, wall);
  });
  // --------------------------------------
  // SECOND PASS — Non-wall assets to fetch
  // --------------------------------------
  const models2DToFetch: Model2DModel[] = [];
  const productsToFetch: ProductModel[] = [];
  const elementsToFetch: ElementModel[] = [];
  const floorsToFetch: FloorModel[] = [];

  const model2DUuids = new Set<string>();
  const productUuids = new Set<string>();
  const elementUuids = new Set<string>();

  // From spaces
  buildingData.levels.forEach((level) => {
    level.rooms.forEach((room) => {
      room.spaces.forEach((space) => {
        // Models2D
        space.models2D.forEach((m) => {
          if (m.uuid && model2DUuids.has(m.uuid)) return;

          const position = new Vector3(m.position.x, m.position.y, m.position.z);
          const model = new Model2DModel(
            m.id,
            m.name,
            m.filePath,
            m.fileFormat,
            m.fileSize,
            1,
            m.dimensions.depth,
            createObject(m.object),
            position,
            m.rotation,
            m.texture,
            createAttributes(m.attributes),
          );
          model.setUUID(m.uuid);
          models2DToFetch.push(model);
          if (m.uuid) model2DUuids.add(m.uuid);
        });

        // Products
        space.products.forEach((p) => {
          if (p.uuid && productUuids.has(p.uuid)) return;

          const position = new Vector3(p.position.x, p.position.y, p.position.z);
          const product = new ProductModel(
            String(p.id),
            p.name,
            p.svgPath,
            1,
            p.dimensions.height ?? 0,
            p.dimensions.depth,
            position,
            p.rotation,
            p.fillColor,
            p.strokeColor,
            p.strokeWidth,
            p.texture,
            createAttributes(p.attributes),
            createObjects(p.objects),
            p.description,
            p.manufacturer,
          );
          product.setUUID(p.uuid);
          productsToFetch.push(product);
          if (p.uuid) productUuids.add(p.uuid);
        });

        // Elements
        space.elements.forEach((e) => {
          if (e.uuid && elementUuids.has(e.uuid)) return;

          const position = new Vector3(e.position.x, e.position.y, e.position.z);
          const element = new ElementModel(
            e.type,
            e.length,
            e.width,
            position,
            e.rotation,
            e.texture,
            e.description,
            e.useAsTextField,
            e.fontSize,
            e.object ? createObject(e.object) : undefined,
            createAttributes(e.attributes),
            e.sideC,
            e.triangleAngle
          );
          element.setUUID(e.uuid);
          elementsToFetch.push(element);
          if (e.uuid) elementUuids.add(e.uuid);
        });

        // Floor (Single 1:1)
        if (space.floor) {
          const f = space.floor;
          const tiles = f.tiles
            ? new TilesModel(f.tiles.length, f.tiles.width, f.tiles.rotation, f.tiles.jointWidth, f.tiles.jointColor, createAttributes(f.tiles.attributes))
            : undefined;

          const floor = new FloorModel(tiles, f.texture, createAttributes(f.attributes));
          floor.setUUID(f.uuid);
          floorsToFetch.push(floor);
        }
      });
    });
  });

  // From levels (floating/root per-level, new structure)
  buildingData.levels.forEach((level) => {
    const lvlAny = level as any;

    (lvlAny.models2D ?? []).forEach((m: any) => {
      if (m.uuid && model2DUuids.has(m.uuid)) return;

      const position = new Vector3(m.position.x, m.position.y, m.position.z);
      const model = new Model2DModel(
        m.id,
        m.name,
        m.filePath,
        m.fileFormat,
        m.fileSize,
        1,
        m.dimensions.depth,
        createObject(m.object),
        position,
        m.rotation,
        m.texture,
        createAttributes(m.attributes),
      );
      model.setUUID(m.uuid);
      models2DToFetch.push(model);
      if (m.uuid) model2DUuids.add(m.uuid);
    });

    (lvlAny.products ?? []).forEach((p: any) => {
      if (p.uuid && productUuids.has(p.uuid)) return;

      const position = new Vector3(p.position.x, p.position.y, p.position.z);
      const product = new ProductModel(
        String(p.id),
        p.name,
        p.svgPath,
        1,
        p.dimensions.height ?? 0,
        p.dimensions.depth,
        position,
        p.rotation,
        p.fillColor,
        p.strokeColor,
        p.strokeWidth,
        p.texture,
        createAttributes(p.attributes),
        createObjects(p.objects),
        p.description,
        p.manufacturer,
      );
      product.setUUID(p.uuid);
      productsToFetch.push(product);
      if (p.uuid) productUuids.add(p.uuid);
    });

    (lvlAny.elements ?? []).forEach((e: any) => {
      if (e.uuid && elementUuids.has(e.uuid)) return;

      const position = new Vector3(e.position.x, e.position.y, e.position.z);
      const element = new ElementModel(
        e.type,
        e.length,
        e.width,
        position,
        e.rotation,
        e.texture,
        e.description,
        e.useAsTextField,
        e.fontSize,
        e.object ? createObject(e.object) : undefined,
        createAttributes(e.attributes),
        e.sideC,
        e.triangleAngle
      );
      element.setUUID(e.uuid);
      elementsToFetch.push(element);
      if (e.uuid) elementUuids.add(e.uuid);
    });
  });

  // Backwards compatibility: legacy root products/elements/models2D on building
  (buildingAny.models2D ?? []).forEach((m: any) => {
    if (m.uuid && model2DUuids.has(m.uuid)) return;

    const position = new Vector3(m.position.x, m.position.y, m.position.z);
    const model = new Model2DModel(
      m.id,
      m.name,
      m.filePath,
      m.fileFormat,
      m.fileSize,
      1,
      m.dimensions.depth,
      createObject(m.object),
      position,
      m.rotation,
      m.texture,
      createAttributes(m.attributes),
    );
    model.setUUID(m.uuid);
    models2DToFetch.push(model);
    if (m.uuid) model2DUuids.add(m.uuid);
  });

  (buildingAny.products ?? []).forEach((p: any) => {
    if (p.uuid && productUuids.has(p.uuid)) return;

    const position = new Vector3(p.position.x, p.position.y, p.position.z);
    const product = new ProductModel(
      String(p.id),
      p.name,
      p.svgPath,
      1,
      p.dimensions.height ?? 0,
      p.dimensions.depth,
      position,
      p.rotation,
      p.fillColor,
      p.strokeColor,
      p.strokeWidth,
      p.texture,
      createAttributes(p.attributes),
      createObjects(p.objects),
      p.description,
      p.manufacturer,
    );
    product.setUUID(p.uuid);
    productsToFetch.push(product);
    if (p.uuid) productUuids.add(p.uuid);
  });

  (buildingAny.elements ?? []).forEach((e: any) => {
    if (e.uuid && elementUuids.has(e.uuid)) return;

    const position = new Vector3(e.position.x, e.position.y, e.position.z);
    const element = new ElementModel(
      e.type,
      e.length,
      e.width,
      position,
      e.rotation,
      e.texture,
      e.description,
      e.useAsTextField,
      e.fontSize,
      e.object ? createObject(e.object) : undefined,
      createAttributes(e.attributes),
      e.sideC,
      e.triangleAngle
    );
    element.setUUID(e.uuid);
    elementsToFetch.push(element);
    if (e.uuid) elementUuids.add(e.uuid);
  });

  // ----------------------------
  // FETCH MODELS2D
  // ----------------------------
  const fetchedModelsRaw = await Promise.all(
    models2DToFetch.map(async (modelData) =>
      auth
        ? instantiateModel2DAsync(modelData.id, modelData.position, modelData.rotation, modelData.texture, modelData)
        : {
            model2D: new Model2DModel(modelData.id, '', '', '', 0, 0, 0, null as any, modelData.position, 0, null, []),
            modelFetchingSuccesful: false,
            textureFetchingSuccesful: false,
            neededToFetchTexture: false,
            modelId: String(modelData.id),
            textureId: String(modelData.texture?.id ?? '-1'),
            modelData,
          },
    ),
  );

  const models2D: Model2DModel[] = [];
  fetchedModelsRaw.forEach((res) => {
    if (res.model2D) {
      models2D.push(res.model2D);

      if (!res.textureFetchingSuccesful && res.textureId !== '-1') {
        nonFetchableEntities.push({
          objectType: 'notFetchableEntity',
          modelType: 'model2D',
          type: 'texture',
          id: res.textureId,
          entityData: res.modelData,
          subType: undefined
        });
      }
    } else {
      nonFetchableEntities.push({
        objectType: 'notFetchableEntity',
        modelType: 'model2D',
        type: 'modelData',
        id: res.modelId,
        entityData: res.modelData,
        subType: undefined
      });
    }
  });

  // ----------------------------
  // FETCH PRODUCTS
  // ----------------------------
  const prevNonFetchProducts = prevNonFetch.filter((n) => n.modelType === 'product').map((n) => n.entityData);
  const prevNonFetchIds = new Set(prevNonFetchProducts.map((n) => String(n.id)));
  const mergedProducts = productsToFetch.filter((p) => !prevNonFetchIds.has(p.id)).concat(prevNonFetchProducts);

  const fetchedProductsRaw = await Promise.all(
    mergedProducts.map(async (productData) => (auth ? fetchProductById(productData.id, productData.position, productData.rotation, productData, productData.texture) : null)),
  );

  const products: ProductModel[] = [];
  fetchedProductsRaw.forEach((res) => {
    if (!res) return;

    if ((res as any).objectType === 'notFetchableEntity') {
      nonFetchableEntities.push(res as NonFetchableEntity);
    } else if ('product' in res) {
      products.push(res.product);

      if (!res.textureFetchingSuccesful && res.textureId !== '-1') {
        nonFetchableEntities.push({
          objectType: 'notFetchableEntity',
          modelType: 'product',
          type: 'texture',
          id: res.textureId,
          subType: res.type,
          entityData: res.entityData,
        });
      }
    } else {
      products.push(res as ProductModel);
    }
  });

  // ----------------------------
  // FETCH ELEMENTS
  // ----------------------------
  const prevNonFetchElements = prevNonFetch.filter((e) => e.modelType === 'element').map((e) => e.entityData);
  const prevNonFetchElemIds = new Set(prevNonFetchElements.map((e) => e.uuid));
  const mergedElements = elementsToFetch.filter((el) => !prevNonFetchElemIds.has(el.uuid)).concat(prevNonFetchElements);

  const fetchedElementsRaw = await Promise.all(mergedElements.map(async (e) => instantiateElementAsync(e)));

  const elements: ElementModel[] = [];
  fetchedElementsRaw.forEach((res) => {
    if (res.neededToFetchTexture && !res.fetchingSuccesful && res.textureId !== '-1') {
      nonFetchableEntities.push({
        objectType: 'notFetchableEntity',
        modelType: 'element',
        type: 'texture',
        id: res.textureId,
        entityData: res.elementData,
      });
    }
    elements.push(res.element);
  });

  // ----------------------------
  // FLOORS
  // ----------------------------
  const floors: FloorModel[] = [];
  for (const f of floorsToFetch) {
    let floorTexture: ITexture | null = null;

    const isLegacy = f.texture?.id === -1 && f.texture?.color;

    if (isLegacy) {
      floorTexture = {
        id: -1,
        name: '',
        material: null,
        color: f.texture!.color,
        rotation: 0,
        offset: 0,
      };
    } else if (auth) {
      let texData = f.texture;
      const saved = prevNonFetch.find((n) => n.modelType === 'floor' && n.type === 'texture');
      if (saved) texData = saved.entityData.texture;

      const texRes = await fetchTextureFromApi(texData);
      floorTexture = texRes.texture;

      if (!texRes.fetchingSuccesful && texRes.neededToFetchTexture) {
        nonFetchableEntities.push({
          objectType: 'notFetchableEntity',
          modelType: 'floor',
          type: 'texture',
          id: f.texture?.id ?? '-1',
          entityData: f,
        });
      }
    }

    const floorModel = new FloorModel(f.tiles, floorTexture, createAttributes(f.attributes));
    floorModel.setUUID(f.uuid);
    floors.push(floorModel);
  }

  // -------------------------------------------------------
  // THIRD PASS — BUILD FULL ROOM + SPACE + BOUNDARIES + LEVEL FLOATS
  // -------------------------------------------------------
  const levels: BuildingLevelModel[] = [];

  buildingData.levels.forEach((level) => {
    const levelRooms: RoomModel[] = [];

    level.rooms.forEach((room) => {
      const roomSpaces: SpaceModel[] = [];

      room.spaces.forEach((space) => {
        // Clone rebuilt assets by UUID
        const floorData = space.floor;
        const spaceFloor = floorData ? floors.find((ff) => ff.uuid === floorData.uuid) : undefined;

        const spaceProducts: ProductModel[] = space.products.map((p) => products.find((pp) => pp.uuid === p.uuid)).filter(Boolean) as ProductModel[];

        const spaceElements: ElementModel[] = space.elements.map((e) => elements.find((ee) => ee.uuid === e.uuid)).filter(Boolean) as ElementModel[];

        const spaceModels2D: Model2DModel[] = space.models2D.map((m) => models2D.find((mm) => mm.uuid === m.uuid)).filter(Boolean) as Model2DModel[];

        // Walls: reference global wallMap
        const spaceWalls: Wall[] = space.walls.map((w) => wallMap.get(w.uuid)).filter(Boolean) as Wall[];

        // Boundaries
        const spaceBoundaries: Boundary[] = [];

        (space.boundaries ?? []).forEach((b) => {
          let boundary: Boundary;

          if (b.type === BOUNDARY_TYPE.PHYSICAL) {
            const physData = b as any;

            const pb = new PhysicalBoundaryModel();
            pb.setUUID(b.uuid);

            // legacy list of walls
            if (Array.isArray(physData.walls)) {
              const wallInstances = physData.walls.map((w: any) => wallMap.get(typeof w === 'string' ? w : w.uuid)).filter(Boolean);
              pb.setWalls(wallInstances);
            }

            // new edges
            if (Array.isArray(physData.edges)) {
              const edges = physData.edges
                .map((edgeData: any) => {
                  // Handle explicit wall object or ID string
                  const wallRef = edgeData.wall?.uuid ?? edgeData.wall;
                  const wall = wallMap.get(wallRef);

                  // Ensure wall exists before creating edge (optional safety check)
                  if (!wall) return null;

                  const e = new BoundaryEdge(wall, edgeData.t0, edgeData.t1, edgeData.side, !!edgeData.reversed);
                  e.setUUID(edgeData.uuid);
                  return e;
                })
                .filter((e: any) => e !== null); // Filter out nulls if wall wasn't found

              pb.setEdges(edges);
            }

            boundary = pb;
          } else {
            const vb = new VirtualBoundaryModel();
            vb.setUUID(b.uuid);
            boundary = vb;
          }

          spaceBoundaries.push(boundary);
        });

        const spaceModel = new SpaceModel(spaceFloor, spaceProducts, spaceElements, spaceModels2D, spaceBoundaries, spaceWalls);
        spaceModel.setUUID(space.uuid);
        roomSpaces.push(spaceModel);
      });

      const roomModel = new RoomModel(room.title, roomSpaces, createAttributes(room.attributes));
      roomModel.setUUID(room.uuid);
      levelRooms.push(roomModel);
    });

    // LEVEL FLOATING ARRAYS
    const lvlAny = level as any;

    const levelWalls: Wall[] =
      (lvlAny.walls ?? [])
        .map((w: any) => wallMap.get(w.uuid))
        .filter(Boolean) as Wall[];

    const levelProducts: ProductModel[] =
      (lvlAny.products ?? [])
        .map((p: any) => products.find((pp) => pp.uuid === p.uuid))
        .filter(Boolean) as ProductModel[];

    const levelElements: ElementModel[] =
      (lvlAny.elements ?? [])
        .map((e: any) => elements.find((ee) => ee.uuid === e.uuid))
        .filter(Boolean) as ElementModel[];

    const levelModels2D: Model2DModel[] =
      (lvlAny.models2D ?? [])
        .map((m: any) => models2D.find((mm) => mm.uuid === m.uuid))
        .filter(Boolean) as Model2DModel[];

    const levelModel = new BuildingLevelModel(
      level.level,
      levelRooms,
      new CompassModel(compassRotation),
      levelWalls,
      levelProducts,
      levelElements,
      levelModels2D,
    );
    levelModel.setUUID(level.uuid);
    levels.push(levelModel);
  });

  // ----------------------------
  // BUILD BUILDING MODEL
  // ----------------------------
  const importedBuilding = new BuildingModel([]);
  importedBuilding.setLevels(levels);

  // ----------------------------
  // Measurements + Sprites + Hotspots
  // ----------------------------
  const measurements: SimpleMeasurementModel[] = planData.annotations?.[0]?.measurements?.map(deserializeMeasurement) ?? [];

  const cameraPositionSprites: CameraPositionSpriteModel[] =
    planData.annotations?.[0]?.cameraPositionSprites?.map(
      (s: any) => new CameraPositionSpriteModel(s.id, new Vector3(s.position.x, s.position.y, s.position.z), s.spaceUuid ?? null),
    ) ?? [];

  const hotspots: HotspotModel[] = planData.annotations?.[0]?.hotspots ? await Promise.all(planData.annotations[0].hotspots.map(deserializeHotspot)) : [];

  // ----------------------------
  // FINAL CONTEXT
  // ----------------------------
  const building = transformMultiRoomStructure(importedBuilding, compassRotation, nonFetchableEntities, relations);

  const plan = new PlanModel(
    planData.name,
    planData.id,
    planData.kojeId,
    planData.kojeLevel,
    planData.eventId,
    planData.dataObjectId,
    planData.showroomId,
    planData.appVersion,
    building,
    planData.jsonVersion,
    planData.region ?? '',
  );

  return {
    plan,
    building,
    measurements,
    cameraPositionSprites,
    hotspots,
    nonFetchableEntities,
    relations,
  };
};
